import { BatonMark } from "@baton/brand";
import { clientHubSectionsKey, sidebarWidthKey } from "@baton/core";
import { useI18n } from "@baton/i18n";
import { useQuery } from "@tanstack/react-query";
import { LoginCard, SkeletonBlock, SkeletonRegion } from "@baton/ui";
import type { CSSProperties, ReactNode } from "react";
import { useParams } from "react-router";
import { meQuery } from "../app/queries.ts";
import { useRuntime } from "../app/RuntimeProvider.tsx";
import { AppShell, SIDEBAR_DEFAULT } from "../shell/layout/AppShell.tsx";
import { Sidebar } from "../shell/sidebar/Sidebar.tsx";
import { readsClients } from "../shell/state/access.ts";
import { isWorkspaceMember } from "../shell/state/permissions.ts";
import "./skeletons.css";

/**
 * FR-015-090, `contracts/screens.md` § States of every screen: the loading state of each
 * screen as a skeleton of its final layout, built from the design-system Skeleton blocks and
 * announced once through one `SkeletonRegion`. The route table renders these as the lazy
 * chunk fallbacks, so this module sits in the initial chunk, and each screen renders the same
 * skeleton, or the part of it that belongs to one panel, while its own reads are pending.
 */
export type SkeletonPresentation = "peek" | "modal" | "page";

function times(count: number, render: (index: number) => ReactNode): ReactNode[] {
  return Array.from({ length: count }, (_, index) => render(index));
}

/** A page's title, alone: no page carries a lede under its title (2026-09-27, the owner's standing rule; 015 `contracts/screens.md` § Page titles). */
function PageHead({ inset = false }: { readonly inset?: boolean }) {
  return (
    <div className={inset ? "b-loading__page-head b-loading__page-head--inset" : "b-loading__page-head"}>
      <SkeletonBlock className="b-loading__heading" />
    </div>
  );
}

/** A list page's toolbar row under its title (2026-09-27): the one search and filter field, then `controls` quiet icon controls at its end. */
function ListToolbar({ controls }: { readonly controls: number }) {
  return (
    <div className="b-loading__filters">
      <SkeletonBlock className="b-loading__search" />
      <span className="b-loading__toolbar-end">
        {times(controls, (control) => (
          <SkeletonBlock key={control} className="b-loading__icon-control" />
        ))}
      </span>
    </div>
  );
}

function ListRows({ rows }: { readonly rows: number }) {
  return (
    <div>
      <div className="b-loading__group">
        <SkeletonBlock className="b-loading__group-label" />
        <SkeletonBlock className="b-loading__count" />
      </div>
      {times(rows, (row) => (
        <div key={row} className="b-loading__row">
          <SkeletonBlock className="b-loading__glyph" />
          <SkeletonBlock className="b-loading__title" />
          <span className="b-loading__meta">
            <SkeletonBlock className="b-loading__chip" />
            <SkeletonBlock className="b-loading__avatar" />
          </span>
        </div>
      ))}
    </div>
  );
}

/** The collection header's one toolbar row: the search box, Add filter, then Save as view and Display (2026-09-17: the layout switcher is the project head's tabs). */
function Toolbar() {
  return (
    <div className="b-loading__toolbar">
      <div className="b-loading__toolbar-row">
        <SkeletonBlock className="b-loading__search" />
        <SkeletonBlock className="b-loading__control" />
        <span className="b-loading__toolbar-end">
          <SkeletonBlock className="b-loading__control" />
          <SkeletonBlock className="b-loading__control" />
        </span>
      </div>
    </div>
  );
}

/** The saved views screen: its heading over the table's rows (§ Views management § States); `rowsOnly` once the heading is on screen. */
export function ViewsManagementSkeleton({ rowsOnly = false }: { readonly rowsOnly?: boolean }) {
  return (
    <SkeletonRegion {...(rowsOnly ? {} : { className: "b-page" })}>
      {!rowsOnly && <PageHead />}
      <ListRows rows={6} />
    </SkeletonRegion>
  );
}

/** The board body: four column skeletons, each a header over three card skeletons (§ Board layout § States). */
export function BoardColumnsSkeleton() {
  return (
    <SkeletonRegion className="b-loading__board">
      {times(4, (column) => (
        <div key={column} className="b-loading__board-column">
          <div className="b-loading__group">
            <SkeletonBlock className="b-loading__group-label" />
            <SkeletonBlock className="b-loading__count" />
          </div>
          {times(3, (card) => (
            <div key={card} className="b-loading__board-card">
              <SkeletonBlock className="b-loading__title" />
              <span className="b-loading__meta">{times(2, (chip) => <SkeletonBlock key={chip} className="b-loading__chip" />)}</span>
            </div>
          ))}
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** The calendar body: the weekday row over five weeks of seven days, each day's number and an entry on some (§ Calendar layout § States). */
export function CalendarGridSkeleton({ fit = false }: { readonly fit?: boolean }) {
  return (
    <SkeletonRegion className={fit ? "b-loading__calendar b-loading__calendar--fit" : "b-loading__calendar"}>
      <div className="b-loading__calendar-week">{times(7, (day) => <SkeletonBlock key={day} className="b-loading__calendar-weekday" />)}</div>
      {times(5, (week) => (
        <div key={week} className="b-loading__calendar-week b-loading__calendar-days">
          {times(7, (day) => (
            <div key={day} className="b-loading__calendar-day">
              <SkeletonBlock className="b-loading__calendar-number" />
              {(week * 7 + day) % 3 === 0 && <SkeletonBlock className="b-loading__calendar-entry" />}
            </div>
          ))}
        </div>
      ))}
    </SkeletonRegion>
  );
}

/**
 * The meetings calendar's day and week views while their range loads (018 FR-018-282): the time grid
 * in its own geometry, so nothing moves when it arrives (2026-09-30). It fills its host as the grid
 * does, at least seven hours, and its frame is the grid's: the day heads over a whole day of hours, as
 * tall as the room it is given or the day, whichever is less (2026-09-30, the owner's "I don't like the
 * empty space that we have."). The day heads hold a weekday and a date's disc as the grid's do, the
 * gutter is the grid's width with an hour's name on each rule, and the first rule stands where the grid
 * opens its hours, under the room it keeps for that hour's name; a few blocks sit in the columns.
 */
export function TimeGridSkeleton({ days }: { readonly days: number }) {
  const style = { "--time-grid-days": String(days) } as CSSProperties;
  return (
    <SkeletonRegion className="b-loading__time-grid">
      <div className="b-loading__time-grid-frame">
        <div className="b-loading__time-grid-head" style={style}>
          <span />
          {times(days, (day) => (
            <div key={day} className="b-loading__time-grid-day-head">
              <SkeletonBlock className="b-loading__time-grid-weekday" />
              <SkeletonBlock className="b-loading__time-grid-number" />
            </div>
          ))}
        </div>
        <div className="b-loading__time-grid-body" style={style}>
          <div className="b-loading__time-grid-gutter">
            {times(24, (hour) => (
              <span key={hour} className="b-loading__time-grid-at" style={{ "--time-grid-at": String(hour) } as CSSProperties}>
                <SkeletonBlock className="b-loading__time-grid-hour" />
              </span>
            ))}
          </div>
          {times(days, (day) => (
            <div key={day} className="b-loading__time-grid-day">
              {day % 2 === 0 && <SkeletonBlock className="b-loading__time-grid-block" />}
              {day % 3 === 1 && <SkeletonBlock className="b-loading__time-grid-block b-loading__time-grid-block--late" />}
            </div>
          ))}
        </div>
      </div>
    </SkeletonRegion>
  );
}

/** The meetings calendar's Agenda on a phone while its week loads (018 FR-018-285): two days, each its date over rows of a time and a title, as the agenda draws them. */
export function AgendaSkeleton() {
  return (
    <SkeletonRegion className="b-loading__agenda">
      {times(2, (day) => (
        <div key={day} className="b-loading__agenda-day">
          <SkeletonBlock className="b-loading__agenda-date" />
          {times(3, (row) => (
            <div key={row} className="b-loading__agenda-row">
              <SkeletonBlock className="b-loading__agenda-when" />
              <span className="b-loading__agenda-what">
                <SkeletonBlock className="b-loading__agenda-title" />
                <SkeletonBlock className="b-loading__agenda-detail" />
              </span>
            </div>
          ))}
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** The views a meetings calendar's skeleton draws: the time grid over `days`, the month's grid, or the agenda. */
export type MeetingCalendarSkeletonView = { readonly grid: "time"; readonly days: number } | { readonly grid: "month" } | { readonly grid: "agenda" };

/** The body of the meetings calendar's view while it loads: the time grid, the month or the agenda. */
export function MeetingCalendarBodySkeleton({ view }: { readonly view: MeetingCalendarSkeletonView }) {
  // The meetings month fills the viewport below its controls (018 FR-018-305), and so does its skeleton.
  if (view.grid === "month") return <CalendarGridSkeleton fit />;
  if (view.grid === "agenda") return <AgendaSkeleton />;
  return <TimeGridSkeleton days={view.days} />;
}

/**
 * The Meetings page on its Calendar layout before its chunk or its catalogues arrive (018 FR-018-282
 * as amended 2026-09-30, the Calendar being the layout a visit opens): the title, the toolbar, the
 * calendar's controls row, previous, Today, next and the range at its start, the declined meetings'
 * switch and the offers' line, and the views at its end, each row wrapping as the page's own does on a
 * phone, and the view that will mount, so the page does not change shape when it arrives.
 */
export function MeetingsCalendarPageSkeleton({ view }: { readonly view: MeetingCalendarSkeletonView }) {
  return (
    <div className="b-loading__page">
      <PageHead />
      <div className="b-loading__meetings-toolbar">
        <ListToolbar controls={3} />
      </div>
      <div className="b-loading__meetings-calendar">
        <div className="b-loading__calendar-bar">
          <span className="b-loading__calendar-nav">
            <SkeletonBlock className="b-loading__icon-control" />
            <SkeletonBlock className="b-loading__calendar-today" />
            <SkeletonBlock className="b-loading__icon-control" />
            <SkeletonBlock className="b-loading__calendar-range" />
          </span>
          <span className="b-loading__calendar-notes">
            <SkeletonBlock className="b-loading__calendar-switch" />
            <SkeletonBlock className="b-loading__calendar-review" />
          </span>
          <SkeletonBlock className="b-loading__calendar-views" />
        </div>
        <MeetingCalendarBodySkeleton view={view} />
      </div>
    </div>
  );
}

/** The project items route on the board layout before its chunk or its project resolves: the collection toolbar over the columns. */
export function BoardSkeleton() {
  return (
    <div className="b-loading__collection">
      <SkeletonRegion>
        <Toolbar />
      </SkeletonRegion>
      <BoardColumnsSkeleton />
    </div>
  );
}

/** The item list body: a group header and twelve rows of state glyph, title and property chips (§ List layout § States). */
export function ListRowsSkeleton() {
  return (
    <SkeletonRegion>
      <ListRows rows={12} />
    </SkeletonRegion>
  );
}

/** The project item list before its project resolves: the collection toolbar over the rows. */
/** A project page's head (§ Project page): the name row with the identifier tile, then the row of four section tabs. */
function ProjectHead() {
  return (
    <>
      <div className="b-loading__project-head">
        <span className="b-loading__project-name">
          <SkeletonBlock className="b-loading__mark" />
          <SkeletonBlock className="b-loading__title" />
        </span>
        <span className="b-loading__meta">
          <SkeletonBlock className="b-loading__caption" />
          <SkeletonBlock className="b-loading__progress-line" />
          <SkeletonBlock className="b-loading__caption" />
        </span>
      </div>
      <div className="b-loading__tabs-line">{times(3, (tab) => <SkeletonBlock key={tab} className="b-loading__tab-label" />)}</div>
    </>
  );
}

export function ProjectItemsSkeleton() {
  return (
    <SkeletonRegion className="b-loading__collection">
      <ProjectHead />
      <Toolbar />
      <ListRows rows={12} />
    </SkeletonRegion>
  );
}

export function AllItemsSkeleton() {
  return (
    <SkeletonRegion className="b-loading__collection">
      <PageHead inset />
      <Toolbar />
      <ListRows rows={12} />
    </SkeletonRegion>
  );
}

export function MyWorkSkeleton() {
  return (
    <SkeletonRegion className="b-loading__collection">
      <PageHead inset />
      <div className="b-loading__tabs-line">{times(2, (tab) => <SkeletonBlock key={tab} className="b-loading__tab-label" />)}</div>
      <Toolbar />
      <ListRows rows={12} />
    </SkeletonRegion>
  );
}

function InboxRows() {
  return (
    <>
      {times(8, (row) => (
        <div key={row} className="b-loading__inbox-row">
          <SkeletonBlock className="b-loading__icon" />
          <SkeletonBlock className="b-loading__icon" />
          <SkeletonBlock className="b-loading__title" />
          <SkeletonBlock className="b-loading__caption" />
        </div>
      ))}
    </>
  );
}

/** Eight notification rows, drawn as the item list's rows are (§ Inbox § States, 2026-09-17): the mark, the glyph, the title and the time, with no kind column since 2026-10-03. */
export function InboxRowsSkeleton() {
  return (
    <SkeletonRegion>
      <InboxRows />
    </SkeletonRegion>
  );
}

export function InboxSkeleton() {
  return (
    <SkeletonRegion className="b-loading__page">
      <PageHead />
      <div className="b-loading__tabs-line b-loading__tabs-line--page">{times(5, (tab) => <SkeletonBlock key={tab} className="b-loading__tab-label" />)}</div>
      <div className="b-loading__under-tabs">
        <InboxRows />
      </div>
    </SkeletonRegion>
  );
}

function ProjectRows() {
  return (
    <div>
      {times(6, (row) => (
        <div key={row} className="b-loading__project-row">
          <SkeletonBlock className="b-loading__mark" />
          <SkeletonBlock className="b-loading__title" />
          <span className="b-loading__meta">
            <SkeletonBlock className="b-loading__chip" />
            <SkeletonBlock className="b-loading__caption" />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Six row skeletons in an index list: the projects index and a project's modules (§ Projects index § States, § Modules list § States, 2026-09-17). */
export function IndexRowsSkeleton() {
  return (
    <SkeletonRegion>
      <ProjectRows />
    </SkeletonRegion>
  );
}

/** The projects index's grid of six card skeletons (2026-09-17). */
function ProjectCards() {
  return (
    <div className="b-loading__project-cards">
      {times(6, (card) => (
        <div key={card} className="b-loading__project-card">
          <span className="b-loading__project-name">
            <SkeletonBlock className="b-loading__mark" />
            <SkeletonBlock className="b-loading__title" />
          </span>
          {/* The card's parts in its own order, so the grid keeps its row height as the cards arrive (2026-09-18): two lines of description, the sprint's name over its bar, the rule, then the lead, the members and the start. */}
          <span className="b-loading__project-text">
            <SkeletonBlock className="b-loading__line" />
            <SkeletonBlock className="b-loading__line b-loading__line--short" />
          </span>
          <SkeletonBlock className="b-loading__caption" />
          <SkeletonBlock className="b-loading__project-bar" />
          <span className="b-loading__project-foot">
            <SkeletonBlock className="b-loading__chip" />
            <SkeletonBlock className="b-loading__caption" />
            <SkeletonBlock className="b-loading__caption" />
          </span>
        </div>
      ))}
    </div>
  );
}

export function ProjectCardsSkeleton() {
  return (
    <SkeletonRegion>
      <ProjectCards />
    </SkeletonRegion>
  );
}

export function ProjectsIndexSkeleton() {
  return (
    <SkeletonRegion className="b-loading__page">
      <PageHead />
      <ProjectCards />
    </SkeletonRegion>
  );
}

function ActivityRows({ count }: { readonly count: number }) {
  return (
    <div>
      {times(count, (row) => (
        <div key={row} className="b-loading__activity">
          <SkeletonBlock className="b-loading__avatar" />
          <span className="b-loading__stack">
            <SkeletonBlock className="b-loading__caption" />
            <SkeletonBlock className="b-loading__title" />
          </span>
        </div>
      ))}
    </div>
  );
}

/** Activity entries: an avatar beside the actor line and the change line. */
export function ActivityRowsSkeleton({ count }: { readonly count: number }) {
  return (
    <SkeletonRegion>
      <ActivityRows count={count} />
    </SkeletonRegion>
  );
}

/** A page of one activity feed, a client's (`contracts/screens.md` § 020 Accounts and pipeline, Client activity, 2026-09-30): the title, then the feed's rows. */
export function ActivityPageSkeleton() {
  return (
    <SkeletonRegion className="b-loading__page">
      <PageHead />
      <ActivityRows count={10} />
    </SkeletonRegion>
  );
}

/** The Unassigned emails list (022 FR-022-236, 2026-09-28), for the route while its chunk loads: the title, then the rows, each a line of text over a caption as the activity rows are. */
export function UnassignedEmailsSkeleton() {
  return (
    <SkeletonRegion className="b-loading__page">
      <PageHead />
      <ActivityRows count={4} />
    </SkeletonRegion>
  );
}

/** An email's full page (022 FR-022-364 as amended 2026-10-07), for the route while its chunk loads: the title, then the three lines the reader draws while the email is read. */
export function EmailPageSkeleton() {
  return (
    <SkeletonRegion className="b-loading__page">
      <PageHead />
      <SkeletonBlock className="b-loading__lede" />
      <SkeletonBlock className="b-loading__lede" />
      <SkeletonBlock className="b-loading__lede" />
    </SkeletonRegion>
  );
}

/** 022 § Amendment 2026-09-29, the Library: one tab's body while its folder is read, the search and filter field, then two sections, each its heading and a row of document cards (a thumbnail well over a title line and two meta lines), at the grid's own columns. */
export function LibraryTabSkeleton() {
  return (
    <SkeletonRegion className="b-loading__library">
      <ListToolbar controls={0} />
      {times(2, (section) => (
        <div key={section} className="b-loading__library-section">
          <SkeletonBlock className="b-loading__library-heading" />
          <div className="b-loading__library-grid">
            {times(4, (card) => (
              <div key={card} className="b-loading__library-card">
                <SkeletonBlock className="b-loading__library-thumb" />
                <div className="b-loading__library-body">
                  <SkeletonBlock className="b-loading__title" />
                  <SkeletonBlock className="b-loading__caption" />
                  <SkeletonBlock className="b-loading__caption" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** The Library page for the route while its chunk and its tabs load: the title, the line of tabs, then the first tab's body. */
export function LibrarySkeleton() {
  return (
    <SkeletonRegion className="b-loading__page">
      <PageHead />
      <div className="b-loading__tabs-line b-loading__tabs-line--flush">{times(4, (tab) => <SkeletonBlock key={tab} className="b-loading__tab-label" />)}</div>
      <div className="b-loading__under-tabs">
        <LibraryTabSkeleton />
      </div>
    </SkeletonRegion>
  );
}

function CycleRows({ rows }: { readonly rows: number }) {
  return (
    <>
      {times(rows, (row) => (
        <div key={row} className="b-loading__cycle-row">
          <SkeletonBlock className="b-loading__title" />
          <SkeletonBlock className="b-loading__dates" />
          <SkeletonBlock className="b-loading__progress-line" />
        </div>
      ))}
    </>
  );
}

/** The cycles list (§ Cycles list § States): the header, the active sprint's card, then the Upcoming and Completed rows. */
/** The sections of the sprints list, for the page that has drawn its own title and is waiting on its three reads (2026-09-18: it drew the whole page's skeleton inside the page, so a second title sat under the real one at a second inset). */
export function CycleSectionsSkeleton() {
  return (
    <SkeletonRegion>
      <div className="b-loading__section">
        <SkeletonBlock className="b-loading__group-label" />
        <div className="b-loading__cycle-card">
          <SkeletonBlock className="b-loading__title" />
          <SkeletonBlock className="b-loading__caption" />
          <SkeletonBlock className="b-loading__progress-line" />
        </div>
      </div>
      {times(2, (section) => (
        <div key={section} className="b-loading__section">
          <SkeletonBlock className="b-loading__group-label" />
          <CycleRows rows={2} />
        </div>
      ))}
    </SkeletonRegion>
  );
}

/** The whole sprints page, for the route while its chunk loads: the title, then the sections. */
export function CyclesListSkeleton() {
  return (
    <div className="b-loading__page">
      <PageHead />
      <CycleSectionsSkeleton />
    </div>
  );
}

/** The rows of the client and client group tables (020, § 020 Accounts and pipeline § Account list): the header row, then eight rows of `columns` cells, the first a name over its caption; for the page that has drawn its own title and toolbar and is waiting on its list. */
export function AccountRowsSkeleton({ columns }: { readonly columns: number }) {
  return (
    <SkeletonRegion>
      <div className="b-loading__table">
        <div className="b-loading__table-row b-loading__table-row--head">{times(columns - 1, (cell) => <SkeletonBlock key={cell} className="b-loading__caption" />)}</div>
        {times(8, (row) => (
          <div key={row} className="b-loading__table-row">
            <span className="b-loading__stack">
              <SkeletonBlock className="b-loading__title" />
              <SkeletonBlock className="b-loading__caption" />
            </span>
            {times(columns - 2, (cell) => (
              <SkeletonBlock key={cell} className="b-loading__table-cell" />
            ))}
            <SkeletonBlock className="b-loading__glyph" />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** A list page of the Clients area, for the route while its chunk loads (2026-09-27): the title alone, the toolbar row of the search and filter field and its `controls`, then the table's rows. */
export function AccountsPageSkeleton({ columns, controls }: { readonly columns: number; readonly controls: number }) {
  return (
    <div className="b-loading__page">
      <PageHead />
      <ListToolbar controls={controls} />
      <AccountRowsSkeleton columns={columns} />
    </div>
  );
}

/**
 * `contracts/screens.md` § 020 Accounts and pipeline, the block of 2026-09-27, The client hub
 * (States), `contracts/design-system.md` § Empty, the paragraph of 2026-09-28: the hub's loading
 * skeleton draws the sections the hub last drew for this client, which the hub remembers per client
 * (`clientHubSectionsKey`); on a first visit, with nothing remembered, it draws the header, the
 * milestone band and the heads of Tasks, Discussion, Files and Recent activity, the four sections no server
 * build and no reader of the hub leaves out, and every further section appears when the read
 * arrives. The cards are in the hub's own document order and placed as the hub places its
 * sections (`accounts/page/page.css`): the two columns of the owner's review of 2026-09-29 on a wide
 * sheet, the main one Tasks, Calls and meetings, Discussion, Emails and Files and the side one Contacts,
 * Projects, Insights and Recent activity since the owner's second review that day, and
 * below `--bp-lg` one column, the main column's cards and then the side column's. Each entry names
 * the remembered section that draws it.
 */
const HUB_MAIN = [
  { id: "tasks", drawnWith: "tasks", rows: 5 },
  { id: "interactions", drawnWith: "interactions", rows: 2 },
  { id: "discussion", drawnWith: "discussion", rows: 2 },
  { id: "emails", drawnWith: "emails", rows: 3 },
  { id: "files", drawnWith: "files", rows: 1 },
] as const;
const HUB_SIDE = [
  { id: "contacts", drawnWith: "contacts", rows: 2 },
  { id: "projects", drawnWith: "projects", rows: 1 },
  { id: "notes", drawnWith: "notes", rows: 2 },
  { id: "activity", drawnWith: "activity", rows: 3 },
] as const;

/** The milestone band's track: a marker and a caption per milestone, on the hub's skeleton and in the band while the whole stage history is read. */
export function StageTrackSkeleton({ steps }: { readonly steps: number }) {
  return (
    <div className="b-loading__account-track">
      {times(steps, (step) => (
        <span key={step} className="b-loading__account-step">
          <SkeletonBlock className="b-loading__account-marker" />
          <SkeletonBlock className="b-loading__caption" />
        </span>
      ))}
    </div>
  );
}

/** Recent activity, Files, Discussion and Tasks are drawn for every client by every server build and for every reader of the hub (020 FR-020-253, FR-020-309), so a first visit draws their heads too. */
const ALWAYS_DRAWN: ReadonlySet<string> = new Set(["activity", "files", "discussion", "tasks"]);

export function AccountHubSkeleton() {
  const runtime = useRuntime();
  const { ws = "", account = "" } = useParams();
  const drawn = runtime.storage.get(clientHubSectionsKey, `${ws}:${account}`) ?? [];
  const firstVisit = drawn.length === 0;
  const card = (entry: { readonly id: string; readonly drawnWith: string; readonly rows: number }) => (
    <div key={entry.id} className="b-loading__account-card" data-area={entry.id}>
      <div className="b-loading__detail-title-row">
        <SkeletonBlock className="b-loading__group-label" />
        <SkeletonBlock className="b-loading__control" />
      </div>
      {!(firstVisit && ALWAYS_DRAWN.has(entry.drawnWith)) && <AccountSectionRows rows={entry.rows} />}
    </div>
  );
  const shown = (entry: { readonly drawnWith: string }) => drawn.includes(entry.drawnWith) || (firstVisit && ALWAYS_DRAWN.has(entry.drawnWith));
  return (
    <SkeletonRegion className="b-loading__account-page">
      <div className="b-loading__account-head">
        <div className="b-loading__detail-title-row">
          <SkeletonBlock className="b-loading__heading" />
          <span className="b-loading__inline">
            <SkeletonBlock className="b-loading__control" />
            <SkeletonBlock className="b-loading__control" />
          </span>
        </div>
        <span className="b-loading__account-facts">
          {times(5, (fact) => (
            <SkeletonBlock key={fact} className="b-loading__caption" />
          ))}
        </span>
      </div>
      <div className="b-loading__account-card">
        <div className="b-loading__detail-title-row">
          <SkeletonBlock className="b-loading__group-label" />
          <SkeletonBlock className="b-loading__control" />
        </div>
        <StageTrackSkeleton steps={8} />
      </div>
      <div className="b-loading__account-columns">
        {HUB_MAIN.filter(shown).map(card)}
        <div className="b-loading__account-side">{HUB_SIDE.filter(shown).map(card)}</div>
      </div>
    </SkeletonRegion>
  );
}

/** A card of Home's or Overview's: its title over row skeletons. */
function DashCard({ card }: { readonly card: string }) {
  return (
    <div className="b-loading__dash-card" data-card={card}>
      <SkeletonBlock className="b-loading__group-label" />
      {times(4, (row) => (
        <SkeletonBlock key={row} variant="row" />
      ))}
    </div>
  );
}

/**
 * `contracts/screens.md` § Home (2026-10-21): the date title and its line, then Home's cards in its own grid,
 * each a title over row skeletons, fitting one screen as Home does. The cards are the ones the reader will get,
 * decided from `/me` as Home decides them, Today's meetings for a reader of meetings, so the wait never swaps
 * shape (the owner's rule that nothing flashes on refresh). Home draws the same cards under their titles while
 * their reads are pending.
 */
export function HomeSkeleton() {
  const runtime = useRuntime();
  const { ws = "" } = useParams();
  const me = useQuery(meQuery(runtime));
  const meetings = isWorkspaceMember(me.data, ws);
  return (
    <SkeletonRegion className="b-loading__page b-loading__dash">
      <PageHead />
      <span className="b-loading__meta">
        <SkeletonBlock className="b-loading__dash-line" />
      </span>
      <div className="b-loading__dash-you">
        {["waiting", ...(meetings ? ["meetings"] : []), "tasks"].map((card) => (
          <DashCard key={card} card={card} />
        ))}
      </div>
    </SkeletonRegion>
  );
}

/**
 * `contracts/screens.md` § Overview (2026-10-21): the title and its line, the sentence cards, then Clients at
 * risk for a reader of the clients area and Who has what, as Overview lays them out on one screen; the
 * suggestions' sentence card for a reader of meetings.
 */
export function OverviewSkeleton() {
  const runtime = useRuntime();
  const { ws = "" } = useParams();
  const me = useQuery(meQuery(runtime));
  const meetings = isWorkspaceMember(me.data, ws);
  const clients = readsClients(me.data, ws);
  return (
    <SkeletonRegion className="b-loading__page b-loading__dash">
      <PageHead />
      <span className="b-loading__meta">
        <SkeletonBlock className="b-loading__dash-line" />
      </span>
      <div className="b-loading__dash-sentences">
        {times(meetings ? 3 : 2, (card) => (
          <div key={card} className="b-loading__dash-card">
            <SkeletonBlock className="b-loading__group-label" />
            {times(2, (line) => (
              <SkeletonBlock key={line} variant="text" />
            ))}
          </div>
        ))}
      </div>
      <div className="b-loading__dash-lists">
        {[...(clients ? ["clients"] : []), "who"].map((card) => (
          <DashCard key={card} card={card} />
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** The client list on its board layout (`accounts/list/AccountList.tsx`, 2026-09-27): the title, the toolbar row with its `controls`, then the columns; the route's fallback and the board's own pending state draw the same columns, so the wait never swaps shape. */
export function BoardPageSkeleton({ controls }: { readonly controls: number }) {
  return (
    <div className="b-loading__page">
      <PageHead />
      <ListToolbar controls={controls} />
      <BoardColumnsSkeleton />
    </div>
  );
}

/**
 * `contracts/screens.md` § 018 Meeting agent, the meeting page (2026-10-01, the recording-first layout):
 * the header's title and its line of when, the kind, the review's state and the attendees, with the
 * actions at its end; under it the main column, the transcript's timeline, the tab row and the Overview
 * (since 2026-10-02 the summary's lines, the row of action items to review and a section's head over its
 * rows), beside the side panel's transcript head, search and lines, which folds away below 1,280 pixels
 * as the page's own panel does.
 */
export function MeetingPageSkeleton() {
  return (
    <SkeletonRegion className="b-loading__meeting">
      <div className="b-loading__meeting-head">
        <div className="b-loading__meeting-heading">
          <SkeletonBlock className="b-loading__item-title" />
          <span className="b-loading__inline">
            <SkeletonBlock className="b-loading__caption" />
            <SkeletonBlock className="b-loading__chip" />
            <SkeletonBlock className="b-loading__chip" />
            {times(4, (attendee) => (
              <SkeletonBlock key={attendee} className="b-loading__avatar" />
            ))}
          </span>
        </div>
        <span className="b-loading__inline">
          <SkeletonBlock className="b-loading__meeting-button" />
          <SkeletonBlock className="b-loading__meeting-button" />
        </span>
      </div>
      <div className="b-loading__meeting-body">
        <div className="b-loading__meeting-main">
          <SkeletonBlock className="b-loading__meeting-timeline" />
          <SkeletonBlock className="b-loading__item-tabs" />
          <div className="b-loading__meeting-overview">
            <SkeletonBlock className="b-loading__lede" />
            <SkeletonBlock className="b-loading__lede b-loading__meeting-short" />
            <SkeletonBlock className="b-loading__meeting-row" />
            <SkeletonBlock className="b-loading__caption b-loading__meeting-section" />
            {times(4, (row) => (
              <SkeletonBlock key={row} className="b-loading__meeting-row" />
            ))}
          </div>
        </div>
        <div className="b-loading__meeting-side">
          <SkeletonBlock className="b-loading__caption" />
          <SkeletonBlock className="b-loading__meeting-search" />
          {times(8, (line) => (
            <span key={line} className="b-loading__meeting-line">
              <SkeletonBlock className="b-loading__caption" />
              <SkeletonBlock className="b-loading__lede" />
            </span>
          ))}
        </div>
      </div>
    </SkeletonRegion>
  );
}

/** The ruled rows of the hub's sections: a glyph, the name, and the fact at the row's end. */
function AccountSectionRows({ rows }: { readonly rows: number }) {
  return (
    <div>
      {times(rows, (row) => (
        <div key={row} className="b-loading__account-row">
          <SkeletonBlock className="b-loading__glyph" />
          <SkeletonBlock className="b-loading__title" />
          <SkeletonBlock className="b-loading__chip" />
        </div>
      ))}
    </div>
  );
}

/** A progress line as the sprint and module headers draw it: a short bar and its counts (2026-09-17). */
function ProgressLine() {
  return (
    <span className="b-loading__meta">
      <SkeletonBlock className="b-loading__progress-line" />
      <SkeletonBlock className="b-loading__caption" />
    </span>
  );
}

/** The module's progress while its read is pending, one line in the header. */
export function CycleProgressSkeleton() {
  return (
    <SkeletonRegion>
      <ProgressLine />
    </SkeletonRegion>
  );
}

/** The cycle detail and the module detail (§ Cycle detail § States, § Module detail § States): the header with its status, dates and progress line, then the items toolbar and rows. */
/** The head a sprint's page and a module's page share: the name beside its status pill, the page menu at the row's end on a module's page and beside the pill on a sprint's (2026-09-27), the line of dates and progress under it, and the description. */
function DetailHead({ menu = "end", children }: { readonly menu?: "end" | "title"; readonly children?: React.ReactNode }) {
  return (
    <div className="b-loading__detail-head">
      <div className="b-loading__detail-title-row">
        <span className="b-loading__inline">
          <SkeletonBlock className="b-loading__heading" />
          <SkeletonBlock className="b-loading__chip" />
          {menu === "title" && <SkeletonBlock className="b-loading__menu-button b-loading__menu-button--sm" />}
        </span>
        {menu === "end" && <SkeletonBlock className="b-loading__menu-button" />}
      </div>
      {children}
      <span className="b-loading__meta">
        <SkeletonBlock className="b-loading__caption" />
        <SkeletonBlock className="b-loading__progress-line" />
        <SkeletonBlock className="b-loading__caption" />
      </span>
      <SkeletonBlock className="b-loading__lede" />
    </div>
  );
}

/** A sprint's page (`cycle/detail/CycleDetail.tsx`): the head, the list's toolbar and its rows, under the page's own top padding (2026-09-18: the head sat a line too high and had no description or menu, so the page jumped as it loaded). */
export function CycleDetailSkeleton() {
  return (
    <SkeletonRegion className="b-loading__cycle-detail">
      <DetailHead menu="title" />
      <Toolbar />
      <ListRows rows={8} />
    </SkeletonRegion>
  );
}

/** A module's page (`module/detail/ModuleDetail.tsx`), which is a sprint's page with a row of four fields under the name and the Members and Links panels over the list (2026-09-18: it borrowed the sprint's skeleton and had neither). */
export function ModuleDetailSkeleton() {
  return (
    <SkeletonRegion className="b-loading__cycle-detail">
      <DetailHead>
        <div className="b-loading__field-row">
          {times(4, (field) => (
            <span key={field} className="b-loading__field-stack">
              <SkeletonBlock className="b-loading__caption" />
              <SkeletonBlock className="b-loading__control" />
            </span>
          ))}
        </div>
      </DetailHead>
      <div className="b-loading__panel-pair">
        {times(2, (panel) => (
          <div key={panel} className="b-loading__panel">
            <span className="b-loading__detail-title-row">
              <SkeletonBlock className="b-loading__caption" />
              <SkeletonBlock className="b-loading__control" />
            </span>
            <SkeletonBlock className="b-loading__caption" />
          </div>
        ))}
      </div>
      <Toolbar />
      <ListRows rows={6} />
    </SkeletonRegion>
  );
}

/** A short list of item lines (their titles), as the cycle transfer preview reads while its pages load. */
export function ItemLinesSkeleton({ count }: { readonly count: number }) {
  return (
    <SkeletonRegion>
      {times(count, (line) => (
        <div key={line} className="b-loading__item-line">
          <SkeletonBlock className="b-loading__title" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

function SettingsFields() {
  return (
    <div className="b-loading__settings-form">
      <PageHead />
      {times(5, (field) => (
        <div key={field} className="b-loading__settings-field">
          <SkeletonBlock className="b-loading__prop-label" />
          <SkeletonBlock className="b-loading__field" />
        </div>
      ))}
    </div>
  );
}

/** One settings section of one labelled field while its own catalogue loads: the project's Client section (020, § 020 Accounts and pipeline § Account link controls). */
export function SettingsSectionSkeleton() {
  return (
    <SkeletonRegion className="b-loading__settings-form">
      <SkeletonBlock className="b-loading__group-label" />
      <div className="b-loading__settings-field">
        <SkeletonBlock className="b-loading__prop-label" />
        <SkeletonBlock className="b-loading__field" />
      </div>
    </SkeletonRegion>
  );
}

/** The Appearance page (024 § Theme settings) while its theme list is pending: the section's title, then framed rows of two swatch dots, a name with its kind chip and the row's action. */
export function AppearanceSkeleton() {
  return (
    <SkeletonRegion>
      <div className="b-loading__settings-form">
        <PageHead />
        <div>
          {times(3, (row) => (
            <div key={row} className="b-loading__row">
              <SkeletonBlock className="b-loading__glyph" />
              <SkeletonBlock className="b-loading__glyph" />
              <SkeletonBlock className="b-loading__title" />
              <span className="b-loading__meta">
                <SkeletonBlock className="b-loading__chip" />
                <SkeletonBlock className="b-loading__icon-button" />
              </span>
            </div>
          ))}
        </div>
      </div>
    </SkeletonRegion>
  );
}

/**
 * The Milestones settings (020, `contracts/screens.md` § 020 Accounts and pipeline, Milestone templates
 * settings) while the account catalogue or the templates load, under the page's own head: the row of
 * template tabs, then the framed list's band over numbered milestone rows (2026-09-29).
 */
export function MilestoneTemplatesSkeleton() {
  return (
    <SkeletonRegion className="b-loading__milestones">
      <div className="b-loading__milestone-tabs">
        {times(2, (tab) => (
          <SkeletonBlock key={tab} className="b-loading__tab-label" />
        ))}
      </div>
      <div className="b-loading__milestone-frame">
        <div className="b-loading__milestone-band">
          <SkeletonBlock className="b-loading__group-label" />
        </div>
        {times(6, (row) => (
          <div key={row} className="b-loading__milestone-row">
            <SkeletonBlock variant="avatar" className="b-loading__milestone-marker" />
            <SkeletonBlock className="b-loading__title" />
          </div>
        ))}
      </div>
    </SkeletonRegion>
  );
}

/** One settings page's body: the heading and its labelled fields, for a page whose area rail is already on screen or lives in the sidebar. */
export function SettingsPageSkeleton() {
  return (
    <SkeletonRegion>
      <SettingsFields />
    </SkeletonRegion>
  );
}

/**
 * A settings area outside the workspace prefix (§ Profile settings, § Instance administration) while its
 * chunk or the caller's read is pending: `SettingsFrame`'s shell with its page rows and actor card as
 * chrome placeholders, the breadcrumb bar and the page's fields, so the frame does not change on arrival.
 */
export function SettingsFrameSkeleton({ pages }: { readonly pages: number }) {
  const runtime = useRuntime();
  return (
    <AppShell
      sidebar={
        <Sidebar treeKey="loading" switcher={null}>
          <SkeletonRegion>
            {times(pages, (row) => (
              <div key={row} className="b-sidebar__placeholder-row">
                <SkeletonBlock tone="chrome" className="b-sidebar__placeholder-label" />
              </div>
            ))}
          </SkeletonRegion>
        </Sidebar>
      }
      topBar={
        <header className="b-topbar" role="banner">
          <SkeletonBlock className="b-loading__lede" />
        </header>
      }
      sidebarWidth={runtime.storage.get(sidebarWidthKey) ?? SIDEBAR_DEFAULT}
      onSidebarWidthChange={() => undefined}
      drawerOpen={false}
      onDrawerOpenChange={() => undefined}
    >
      <div className="b-page b-settings-page">
        <SettingsPageSkeleton />
      </div>
    </AppShell>
  );
}

/** A settings area with a page rail on the work surface (§ Project settings): the rail's rows beside the page's heading and fields. */
export function SettingsRailSkeleton({ pages }: { readonly pages: number }) {
  return (
    <SkeletonRegion className="b-loading__settings">
      <div className="b-loading__settings-nav">{times(pages, (row) => <SkeletonBlock key={row} className="b-loading__nav-row" />)}</div>
      <SettingsFields />
    </SkeletonRegion>
  );
}

function Properties() {
  return (
    <div className="b-loading__props">
      {[4, 2, 1].map((rows, group) => (
        <div key={group} className="b-loading__prop-group">
          {times(rows, (row) => (
            <div key={row} className="b-loading__prop">
              <SkeletonBlock className="b-loading__prop-label" />
              <SkeletonBlock className="b-loading__prop-value" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

/** The item's properties: the seven rows an item always shows in their three groups (2026-09-17, night), in the properties panel's geometry. */
export function PropertiesSkeleton() {
  return (
    <SkeletonRegion>
      <Properties />
    </SkeletonRegion>
  );
}

/**
 * The item detail (§ Item detail states): the title bar, the description editor beside the
 * property rail's eleven 001 properties, the sub-items and watchers panels, and three feed rows.
 */
/** The item detail in its presentation: the task (crumbs, title, description, properties) and the tab row over three feed rows, side by side in the item dialog and stacked otherwise. */
export function ItemDetailSkeleton({ presentation }: { readonly presentation: SkeletonPresentation }) {
  return (
    <SkeletonRegion className={`b-loading__item b-loading__item--${presentation}`}>
      <div className="b-loading__item-main">
        <SkeletonBlock className="b-loading__crumbs" />
        <SkeletonBlock className="b-loading__item-title" />
        <div className="b-loading__item-body">
          <SkeletonBlock variant="card" className="b-loading__editor" />
          <Properties />
        </div>
      </div>
      <div className="b-loading__item-side">
        <SkeletonBlock className="b-loading__item-tabs" />
        <ActivityRows count={3} />
      </div>
    </SkeletonRegion>
  );
}

/** Onboarding and the create-workspace screen: the brand over the wizard card. */
/**
 * The body of a create dialog while its form's chunk loads: one bar per field and, where the
 * form has one, the description editor. The shell renders it inside the real `Dialog` with the
 * dialog's own title, description and size, so the click opens the final frame at once and only
 * the form's controls arrive later (FR-015-090).
 */
export function DialogFormSkeleton({ fields, editor = false }: { readonly fields: number; readonly editor?: boolean }) {
  return (
    <SkeletonRegion className="b-loading__dialog-form">
      {times(fields, (field) => <SkeletonBlock key={field} className="b-loading__field" />)}
      {editor && <SkeletonBlock className="b-loading__editor" />}
    </SkeletonRegion>
  );
}

/** The no-access panel while the shell asks whether the address was one of the person's Teams before its address changed (001 FR-157), so neither answer flashes before the other. */
export function NoAccessSkeleton() {
  return (
    <SkeletonRegion className="b-no-access">
      <SkeletonBlock className="b-loading__heading" />
      <SkeletonBlock />
    </SkeletonRegion>
  );
}

export function WizardSkeleton() {
  const { t } = useI18n();
  return <LoginCard wide title={t("ui.loading")} loading />;
}

/**
 * The authenticated frame before the shell mounts, drawn with the boot screen's own classes
 * from `public/boot.css`, which `index.html` links, so the frame the document paints before
 * the bundle and the frame React paints while `/` resolves its destination are one drawing.
 */
export function ShellSkeleton() {
  const { t } = useI18n();
  return (
    <div className="b-boot">
      <p className="b-boot__status" role="status">
        {t("ui.loading")}
      </p>
      <div className="b-boot__shell" aria-hidden="true">
        <div className="b-boot__rail">
          <div className="b-boot__rail-head">
            <BatonMark className="b-boot__mark" />
          </div>
        </div>
        <div className="b-boot__sidebar">
          <div className="b-boot__switcher">
            <span className="b-boot__switcher-mark" />
          </div>
          <span className="b-boot__bar b-boot__section" data-chrome />
          <div>
            {times(4, (row) => (
              <div key={row} className="b-boot__row">
                <span className="b-boot__bar" data-chrome />
                <span className="b-boot__bar" data-chrome />
              </div>
            ))}
          </div>
        </div>
        <div className="b-boot__sheet">
          <div className="b-boot__topbar">
            <span className="b-boot__bar b-boot__crumbs" />
          </div>
          <div className="b-boot__page">
            <span className="b-boot__bar b-boot__title" />
            {times(5, (line) => (
              <span key={line} className="b-boot__bar b-boot__line" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
